// /apps/patient/src/pages/programs/SessionLogView.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save,
  Activity,
  Copy,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  PlayCircle,
  X,
  AlertCircle
} from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { patientApiService } from '../../services/api';
import { format } from 'date-fns';

interface ExerciseLogEntry {
  exercise_id: string;
  exercise_name: string;
  module_id?: string;
  module_name?: string;
  prescribed_sets: number;
  prescribed_reps: string | number;
  prescribed_weight?: string;
  completed_sets: (boolean | null)[];
  actual_reps: (number | null)[];
  actual_weight: (string | null)[];
  notes?: string;
  skipped?: boolean;
  skip_reason?: string;
}

interface CardioLogEntry extends ExerciseLogEntry {
  is_cardio?: boolean;
  cardio_config?: any;
  cardio_completed?: boolean;
  cardio_duration_minutes?: number;
  cardio_distance?: number;
  cardio_distance_unit?: 'miles' | 'km' | 'meters';
  cardio_notes?: string;
}


interface ModuleGroup {
  module_id: string;
  module_name: string;
  module_type: string;
  exercises: ExerciseLogEntry[];
  isExpanded: boolean;
}

export function SessionLogView() {
  const { assignmentId, date } = useParams<{ assignmentId: string; date: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState<any>(null);
  const [prescription, setPrescription] = useState<any>(null);
  const [logEntries, setLogEntries] = useState<ExerciseLogEntry[]>([]);
  const [moduleGroups, setModuleGroups] = useState<ModuleGroup[]>([]);
  const [lastSessionData, setLastSessionData] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState<'flat' | 'grouped'>('grouped');

  useEffect(() => {
    if (assignmentId && date) {
      loadSession();
    }
  }, [assignmentId, date]);

  useEffect(() => {
    // Update moduleGroups when logEntries changes
    if (moduleGroups.length > 0) {
      setModuleGroups(prev => prev.map(group => ({
        ...group,
        exercises: logEntries.filter(entry => entry.module_id === group.module_id)
      })));
    }
  }, [logEntries]);

  const loadSession = async () => {
    setLoading(true);
    try {
      // Fetch assignment details
      const assignmentData = await patientApiService.getExerciseAssignment(assignmentId!);
      setAssignment(assignmentData);

      // Fetch prescription based on assignment type
      let prescriptionData;
      if (assignmentData.assignment_type === 'workout_plan') {
        const progress = await patientApiService.getAssignmentProgress(assignmentId!).catch(() => ({ current_week: 1 }));
        const currentWeek = progress.current_week || 1;
        const schedule = await patientApiService.getWorkoutWeekSchedule(
          assignmentData.workout_plan_id,
          currentWeek
        );

        // Convert JS day (0=Sun, 6=Sat) to ISO day (1=Mon, 7=Sun)
        const jsDayOfWeek = new Date(date!).getDay();
        const isoDayOfWeek = jsDayOfWeek === 0 ? 7 : jsDayOfWeek;

        // Apply schedule overrides to find the correct workout for this day
        const scheduleOverrides = assignmentData.schedule_overrides || [];
        const override = scheduleOverrides.find(
          (o: any) => o.week_number === currentWeek && o.new_day === isoDayOfWeek
        );

        let todaySchedule;
        if (override) {
          // Find the workout originally scheduled for the override's original day
          todaySchedule = schedule.find((s: any) => s.day_of_week === override.original_day);
        } else {
          // Check if this day's workout was moved elsewhere
          const movedAway = scheduleOverrides.find(
            (o: any) => o.week_number === currentWeek && o.original_day === isoDayOfWeek
          );
          if (!movedAway) {
            todaySchedule = schedule.find((s: any) => s.day_of_week === isoDayOfWeek);
          }
        }

        prescriptionData = todaySchedule?.exercise_prescription;
      } else if (assignmentData.assignment_type === 'rehab_program') {
        const progress = await patientApiService.getAssignmentProgress(assignmentId!).catch(() => ({ current_phase: 1 }));
        const prescriptions = await patientApiService.getRehabPhaseSchedule(
          assignmentData.rehab_program_id,
          progress.current_phase || 1
        );
        prescriptionData = prescriptions[0]?.exercise_prescription;
      } else {
        prescriptionData = await patientApiService.getExerciseProgram(assignmentData.exercise_prescription_id);
      }

      setPrescription(prescriptionData);

      // Initialize log entries from prescription
      initializeLogEntries(prescriptionData);

      // Try to load last session data for "copy from last" feature
      // This would need an API endpoint to get last completion
      // For now, we'll skip this

    } catch (error) {
      console.error('Error loading session:', error);
    } finally {
      setLoading(false);
    }
  };

  const isCardioExercise = (exercise: any): boolean => {
    return exercise?.is_cardio_activity === true ||
      exercise?.exercise_id?.startsWith('cardio_') ||
      exercise?.id?.startsWith('cardio_');
  };

  const initializeLogEntries = (prescription: any) => {
    const entries: ExerciseLogEntry[] = [];
    const groups: ModuleGroup[] = [];

    if (prescription.modules && prescription.modules.length > 0) {
      // Fitness workout with modules
      prescription.modules.forEach((module: any) => {
        const moduleExercises: ExerciseLogEntry[] = [];

        module.exercises?.forEach((exercise: any) => {
          if (isCardioExercise(exercise)) {
            // Handle cardio exercise
            const entry: CardioLogEntry = {
              exercise_id: exercise.id,
              exercise_name: exercise.official_title || exercise.display_name || 'Cardio',
              module_id: module.id,
              module_name: module.name,
              prescribed_sets: 1, // Cardio doesn't have traditional sets
              prescribed_reps: 0,
              prescribed_weight: undefined,
              completed_sets: [false],
              actual_reps: [null],
              actual_weight: [''],
              notes: '',
              is_cardio: true,
              cardio_config: exercise.parameters?.cardio_config,
              cardio_completed: false,
              cardio_duration_minutes: exercise.parameters?.duration_minutes || 20,
              cardio_distance: 0,
              cardio_distance_unit: 'miles'
            };
            entries.push(entry);
            moduleExercises.push(entry);
          } else {
            // Existing code for regular exercises
            const sets = exercise.parameters?.sets || 3;
            const entry: ExerciseLogEntry = {
              exercise_id: exercise.id,
              exercise_name: exercise.official_title || `Exercise ${exercise.id}`,
              module_id: module.id,
              module_name: module.name,
              prescribed_sets: sets,
              prescribed_reps: exercise.parameters?.reps || 10,
              prescribed_weight: exercise.parameters?.weight,
              completed_sets: Array(sets).fill(false),
              actual_reps: Array(sets).fill(null),
              actual_weight: Array(sets).fill(exercise.parameters?.weight || ''),
              notes: ''
            };
            entries.push(entry);
            moduleExercises.push(entry);
          }
        });

        groups.push({
          module_id: module.id,  // Changed from module.module_id
          module_name: module.name,
          module_type: module.module_type,
          exercises: moduleExercises,
          isExpanded: true
        });
      });
    } else if (prescription.exercises && prescription.exercises.length > 0) {
      // Rehab program with simple exercise list
      prescription.exercises.forEach((exercise: any) => {
        if (isCardioExercise(exercise)) {
          // Handle cardio exercise
          const entry: CardioLogEntry = {
            exercise_id: exercise.id,
            exercise_name: exercise.official_title || exercise.display_name || 'Cardio',
            prescribed_sets: 1,
            prescribed_reps: 0,
            prescribed_weight: undefined,
            completed_sets: [false],
            actual_reps: [null],
            actual_weight: [''],
            notes: '',
            is_cardio: true,
            cardio_config: exercise.parameters?.cardio_config,
            cardio_completed: false,
            cardio_duration_minutes: exercise.parameters?.duration_minutes || 20,
            cardio_distance: 0,
            cardio_distance_unit: 'miles'
          };
          entries.push(entry);
        } else {
          // Existing code for regular exercises
          const sets = exercise.parameters?.sets || 3;
          const entry: ExerciseLogEntry = {
            exercise_id: exercise.id,
            exercise_name: exercise.official_title || `Exercise ${exercise.id}`,
            prescribed_sets: sets,
            prescribed_reps: exercise.parameters?.reps || 10,
            prescribed_weight: exercise.parameters?.weight,
            completed_sets: Array(sets).fill(false),
            actual_reps: Array(sets).fill(null),
            actual_weight: Array(sets).fill(exercise.parameters?.weight || ''),
            notes: ''
          };
          entries.push(entry);
        }
      });
    }

    setLogEntries(entries);
    setModuleGroups(groups);
  };

  const toggleModule = (moduleId: string) => {
    setModuleGroups(prev => prev.map(group =>
      group.module_id === moduleId
        ? { ...group, isExpanded: !group.isExpanded }
        : group
    ));
  };

  const updateSetCompletion = (exerciseId: string, setIndex: number, completed: boolean) => {
    setLogEntries(prev => prev.map(entry => {
      if (entry.exercise_id === exerciseId) {
        const newEntry = { ...entry };
        newEntry.completed_sets[setIndex] = completed;
        // If marking as complete and no reps entered, use prescribed
        if (completed && !newEntry.actual_reps[setIndex]) {
          newEntry.actual_reps[setIndex] = typeof entry.prescribed_reps === 'number'
            ? entry.prescribed_reps
            : parseInt(entry.prescribed_reps.toString().split('-')[0]);
        }
        return newEntry;
      }
      return entry;
    }));
  };

  const updateReps = (exerciseId: string, setIndex: number, reps: number | null) => {
    setLogEntries(prev => prev.map(entry => {
      if (entry.exercise_id === exerciseId) {
        const newEntry = { ...entry };
        newEntry.actual_reps[setIndex] = reps;
        // Auto-check completed if reps entered
        if (reps && reps > 0) {
          newEntry.completed_sets[setIndex] = true;
        }
        return newEntry;
      }
      return entry;
    }));
  };

  const updateWeight = (exerciseId: string, setIndex: number, weight: string) => {
    setLogEntries(prev => prev.map(entry => {
      if (entry.exercise_id === exerciseId) {
        const newEntry = { ...entry };
        newEntry.actual_weight[setIndex] = weight;
        return newEntry;
      }
      return entry;
    }));
  };

  const updateAllWeights = (exerciseId: string, weight: string) => {
    setLogEntries(prev => prev.map(entry => {
      if (entry.exercise_id === exerciseId) {
        const newEntry = { ...entry };
        newEntry.actual_weight = Array(entry.prescribed_sets).fill(weight);
        return newEntry;
      }
      return entry;
    }));
  };

  const updateNotes = (exerciseId: string, notes: string) => {
    setLogEntries(prev => prev.map(entry => {
      if (entry.exercise_id === exerciseId) {
        return { ...entry, notes };
      }
      return entry;
    }));
  };

  const updateCardioCompletion = (exerciseId: string, completed: boolean) => {
    setLogEntries(prev => prev.map(entry => {
      if (entry.exercise_id === exerciseId && (entry as CardioLogEntry).is_cardio) {
        return {
          ...entry,
          cardio_completed: completed
        } as CardioLogEntry;
      }
      return entry;
    }));
  };

  const updateCardioDuration = (exerciseId: string, duration: number) => {
    setLogEntries(prev => {
      // Force new array reference
      return [...prev.map(entry => {
        if (entry.exercise_id === exerciseId && (entry as CardioLogEntry).is_cardio) {
          return {
            ...entry,
            cardio_duration_minutes: duration
          } as CardioLogEntry;
        }
        return entry;
      })];
    });
  };


  const updateCardioDistance = (exerciseId: string, distance: number, unit?: string) => {
    setLogEntries(prev => prev.map(entry => {
      if (entry.exercise_id === exerciseId && (entry as CardioLogEntry).is_cardio) {
        const cardioEntry = entry as CardioLogEntry;
        const result = {
          ...cardioEntry,
          cardio_distance: distance,
          cardio_distance_unit: unit || cardioEntry.cardio_distance_unit
        };
        return result;
      }
      return entry;
    }));
  };

  const markExerciseSkipped = (exerciseId: string, skip: boolean) => {
    setLogEntries(prev => prev.map(entry => {
      if (entry.exercise_id === exerciseId) {
        return {
          ...entry,
          skipped: skip,
          completed_sets: skip ? Array(entry.prescribed_sets).fill(false) : entry.completed_sets
        };
      }
      return entry;
    }));
  };

  const markAllComplete = () => {
    setLogEntries(prev => prev.map(entry => ({
      ...entry,
      completed_sets: Array(entry.prescribed_sets).fill(true),
      actual_reps: entry.actual_reps.map((r, i) =>
        r || (typeof entry.prescribed_reps === 'number'
          ? entry.prescribed_reps
          : parseInt(entry.prescribed_reps.toString().split('-')[0]))
      )
    })));
  };

  const copyFromLast = () => {
    // This would copy data from lastSessionData if available
    alert('Copy from last session feature coming soon!');
  };

  const saveSession = async () => {
    setSaving(true);
    try {
      // Calculate totals
      const totalExercisesPrescribed = logEntries.length;
      const completedExercises = logEntries
        .filter(entry => !entry.skipped && (
          (entry as CardioLogEntry).is_cardio
            ? (entry as CardioLogEntry).cardio_completed
            : entry.completed_sets.some(s => s)
        ))
        .map(entry => {
          if ((entry as CardioLogEntry).is_cardio) {
            const cardioEntry = entry as CardioLogEntry;
            return {
              exercise_id: cardioEntry.exercise_id,
              exercise_name: cardioEntry.exercise_name,
              module_id: cardioEntry.module_id,
              sets_completed: 1,
              reps_completed: [],
              weight_used: [],
              notes: cardioEntry.notes,
              completed_at: new Date().toISOString(),
              is_cardio: true,
              cardio_data: {
                total_duration_seconds: (cardioEntry.cardio_duration_minutes || 0) * 60,
                total_distance: cardioEntry.cardio_distance,
                distance_unit: cardioEntry.cardio_distance_unit,
                cardio_config: cardioEntry.cardio_config
              }
            };
          } else {
            return {
              exercise_id: entry.exercise_id,
              exercise_name: entry.exercise_name,
              module_id: entry.module_id,
              sets_completed: entry.completed_sets.filter(s => s).length,
              reps_completed: entry.actual_reps.filter((r, i) => entry.completed_sets[i] && r !== null),
              weight_used: entry.actual_weight.filter((w, i) => entry.completed_sets[i]),
              notes: entry.notes,
              completed_at: new Date().toISOString()
            };
          }
        });

      const skippedExercises = logEntries
        .filter(entry => entry.skipped)
        .map(entry => ({
          exercise_id: entry.exercise_id,
          reason: entry.skip_reason || 'Skipped',
          skipped_at: new Date().toISOString()
        }));

      // Use the new logExerciseSession that creates both session and exercise records
      await patientApiService.logExerciseSession(assignmentId!, {
        prescription_id: prescription.id,
        session_date: date,
        session_type: 'log',
        total_exercises_prescribed: totalExercisesPrescribed,
        completed_exercises: completedExercises,
        skipped_exercises: skippedExercises,
        session_notes: '', // Could add a session notes field
        session_rating: null // Could add a rating field
      });

      // Navigate back to program
      navigate(`/programs/${assignmentId}`);
    } catch (error) {
      console.error('Error saving session:', error);
      alert('Failed to save session. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const calculateProgress = () => {
    const totalSets = logEntries.reduce((sum, entry) => sum + entry.prescribed_sets, 0);
    const completedSets = logEntries.reduce((sum, entry) =>
      sum + entry.completed_sets.filter(s => s).length, 0
    );
    return {
      completed: completedSets,
      total: totalSets,
      percentage: totalSets > 0 ? Math.round((completedSets / totalSets) * 100) : 0
    };
  };

  const progress = calculateProgress();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!prescription) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Session Error" showBack />
        <div className="text-center py-8">
          <p className="text-gray-600">Unable to load workout</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold">{prescription.name || 'Workout Log'}</h1>
              <p className="text-sm text-gray-600">{format(new Date(date!), 'EEEE, MMMM d, yyyy')}</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-sm">
                <span className="text-gray-600">Progress: </span>
                <span className="font-medium">{progress.completed}/{progress.total} sets</span>
                <span className="text-gray-500"> ({progress.percentage}%)</span>
              </div>
              <button
                onClick={() => navigate(`/programs/${assignmentId}/session/${date}?mode=switch`)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Switch to Interactive Mode"
              >
                <PlayCircle className="h-5 w-5" />
              </button>
              <button
                onClick={() => navigate(`/programs/${assignmentId}`)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="bg-white border-b px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={markAllComplete}
              className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm flex items-center space-x-1"
            >
              <CheckCircle className="h-4 w-4" />
              <span>Mark All Complete</span>
            </button>
            <button
              onClick={copyFromLast}
              disabled={!lastSessionData}
              className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Copy className="h-4 w-4" />
              <span>Copy Last Session</span>
            </button>
          </div>
          {moduleGroups.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">View:</span>
              <button
                onClick={() => setViewMode('grouped')}
                className={`px-3 py-1 text-sm rounded ${viewMode === 'grouped' ? 'bg-primary text-white' : 'bg-gray-100'
                  }`}
              >
                By Module
              </button>
              <button
                onClick={() => setViewMode('flat')}
                className={`px-3 py-1 text-sm rounded ${viewMode === 'flat' ? 'bg-primary text-white' : 'bg-gray-100'
                  }`}
              >
                All Exercises
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Grouped View (Modules) */}
        {viewMode === 'grouped' && moduleGroups.length > 0 ? (
          <div className="space-y-4">
            {moduleGroups.map(group => (
              <div key={group.module_id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleModule(group.module_id)}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <Activity className="h-5 w-5 text-gray-600" />
                    <div className="text-left">
                      <h3 className="font-medium">{group.module_name}</h3>
                      <p className="text-sm text-gray-600">
                        {group.exercises.length} exercises • {group.module_type}
                      </p>
                    </div>
                  </div>
                  {group.isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {group.isExpanded && (
                  <div className="p-4">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b text-sm text-gray-600">
                            <th className="text-left pb-2">Exercise</th>
                            <th className="text-center pb-2">Target</th>
                            {Array.from({ length: Math.max(...group.exercises.map(e => e.prescribed_sets)) }, (_, i) => (
                              <th key={i} className="text-center pb-2 min-w-[100px]">Set {i + 1}</th>
                            ))}
                            <th className="text-center pb-2">Notes</th>
                            <th className="text-center pb-2">Skip</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.exercises.map(entry => (
                            (entry as CardioLogEntry).is_cardio ? (
                              <CardioRow
                                key={entry.exercise_id}
                                entry={entry as CardioLogEntry}
                                onUpdateNotes={updateNotes}
                                onSkip={markExerciseSkipped}
                                onUpdateCompletion={updateCardioCompletion}
                                onUpdateDuration={updateCardioDuration}
                                onUpdateDistance={updateCardioDistance}
                              />
                            ) : (
                              <ExerciseRow
                                key={entry.exercise_id}
                                entry={entry}
                                onUpdateSet={updateSetCompletion}
                                onUpdateReps={updateReps}
                                onUpdateWeight={updateWeight}
                                onUpdateAllWeights={updateAllWeights}
                                onUpdateNotes={updateNotes}
                                onSkip={markExerciseSkipped}
                              />
                            )
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Flat View (All Exercises)
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-sm text-gray-600">
                    <th className="text-left pb-2">Exercise</th>
                    <th className="text-center pb-2">Target</th>
                    {Array.from({ length: Math.max(...logEntries.map(e => e.prescribed_sets)) }, (_, i) => (
                      <th key={i} className="text-center pb-2 min-w-[100px]">Set {i + 1}</th>
                    ))}
                    <th className="text-center pb-2">Notes</th>
                    <th className="text-center pb-2">Skip</th>
                  </tr>
                </thead>
                <tbody>
                  {logEntries.map(entry => (
                    (entry as CardioLogEntry).is_cardio ? (
                      <CardioRow
                        key={entry.exercise_id}
                        entry={entry as CardioLogEntry}
                        onUpdateNotes={updateNotes}
                        onSkip={markExerciseSkipped}
                        onUpdateCompletion={updateCardioCompletion}
                        onUpdateDuration={updateCardioDuration}
                        onUpdateDistance={updateCardioDistance}
                      />
                    ) : (
                      <ExerciseRow
                        key={entry.exercise_id}
                        entry={entry}
                        maxSets={Math.max(...logEntries.map(e => e.prescribed_sets))}
                        onUpdateSet={updateSetCompletion}
                        onUpdateReps={updateReps}
                        onUpdateWeight={updateWeight}
                        onUpdateAllWeights={updateAllWeights}
                        onUpdateNotes={updateNotes}
                        onSkip={markExerciseSkipped}
                      />
                    )
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {progress.completed > 0 && (
              <span>
                <AlertCircle className="h-4 w-4 inline mr-1" />
                Remember to save your progress
              </span>
            )}
          </div>
          <button
            onClick={saveSession}
            disabled={saving || progress.completed === 0}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <Save className="h-5 w-5" />
            <span>{saving ? 'Saving...' : 'Save Workout'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Cardio row component
function CardioRow({
  entry,
  onUpdateNotes,
  onSkip,
  onUpdateCompletion,
  onUpdateDuration,
  onUpdateDistance
}: {
  entry: CardioLogEntry;
  onUpdateNotes: (exerciseId: string, notes: string) => void;
  onSkip: (exerciseId: string, skip: boolean) => void;
  onUpdateCompletion: (exerciseId: string, completed: boolean) => void;
  onUpdateDuration: (exerciseId: string, duration: number) => void;
  onUpdateDistance: (exerciseId: string, distance: number, unit: string) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const config = entry.cardio_config || {
    format: 'simple',
    simple: {
      duration_minutes: 20,
      intensity: 'moderate'
    }
  };

  const getCardioSummary = () => {
    if (config.format === 'simple') {
      return `${config.simple?.duration_minutes || 20} min @ ${config.simple?.intensity || 'moderate'}`;
    } else if (config.format === 'intervals') {
      return `Intervals: ${config.intervals?.repeat_count || 1} rounds`;
    } else if (config.format === 'structured') {
      return `${config.structured?.segments?.length || 0} segments`;
    }
    return 'Cardio';
  };

  return (
    <tr className={`border-b ${entry.skipped ? 'opacity-50' : ''}`}>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-blue-600" />
          <div>
            <p className="font-medium text-sm">{entry.exercise_name}</p>
            {entry.module_name && (
              <p className="text-xs text-gray-500">{entry.module_name}</p>
            )}
          </div>
        </div>
      </td>
      <td className="text-center text-sm">
        <p>{getCardioSummary()}</p>
      </td>
      <td colSpan={3} className="px-2">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-gray-100 rounded flex items-center gap-1"
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
            <span className="text-sm">Log Cardio</span>
          </button>

          {!isExpanded && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={entry.cardio_completed || false}
                onChange={(e) => onUpdateCompletion(entry.exercise_id, e.target.checked)}
                disabled={entry.skipped}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-600">
                {entry.cardio_completed && entry.cardio_duration_minutes ?
                  `${entry.cardio_duration_minutes} min` : 'Not completed'}
                {entry.cardio_completed && entry.cardio_distance ?
                  ` • ${entry.cardio_distance} ${entry.cardio_distance_unit}` : ''}
              </span>
            </div>
          )}
        </div>

        {isExpanded && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  value={entry.cardio_duration_minutes || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty string and parse as number only when not empty
                    const duration = value === '' ? 0 : parseFloat(value);
                    onUpdateDuration(entry.exercise_id, duration);
                    if (duration > 0) {
                      onUpdateCompletion(entry.exercise_id, true);
                    }
                  }}
                  className="w-full px-3 py-1 border rounded-md"
                  placeholder="20"
                  disabled={entry.skipped}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Distance
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.1"
                    value={(() => {
                      return entry.cardio_distance || '';
                    })()}
                    onChange={(e) => {
                      const distance = parseFloat(e.target.value) || 0;
                      onUpdateDistance(entry.exercise_id, distance, entry.cardio_distance_unit || 'miles');
                    }}
                    className="flex-1 px-3 py-1 border rounded-md"
                    placeholder="0"
                    disabled={entry.skipped}
                  />
                  <select
                    value={entry.cardio_distance_unit || 'miles'}
                    onChange={(e) => {
                      onUpdateDistance(entry.exercise_id, entry.cardio_distance || 0, e.target.value);
                    }}
                    className="px-2 py-1 border rounded-md"
                    disabled={entry.skipped}
                  >
                    <option value="miles">miles</option>
                    <option value="km">km</option>
                    <option value="meters">meters</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={entry.notes || ''}
                onChange={(e) => onUpdateNotes(entry.exercise_id, e.target.value)}
                className="w-full px-3 py-1 border rounded-md"
                rows={2}
                placeholder="How did it feel?"
                disabled={entry.skipped}
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={entry.cardio_completed || false}
                  onChange={(e) => onUpdateCompletion(entry.exercise_id, e.target.checked)}
                  disabled={entry.skipped}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm font-medium">Mark as Complete</span>
              </label>

              <button
                onClick={() => onSkip(entry.exercise_id, !entry.skipped)}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                {entry.skipped ? 'Unskip' : 'Skip Exercise'}
              </button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}

// Exercise Row Component
function ExerciseRow({
  entry,
  onUpdateSet,
  onUpdateReps,
  maxSets,
  onUpdateWeight,
  onUpdateAllWeights,
  onUpdateNotes,
  onSkip
}: {
  entry: ExerciseLogEntry;
  maxSets?: number;
  onUpdateSet: (exerciseId: string, setIndex: number, completed: boolean) => void;
  onUpdateReps: (exerciseId: string, setIndex: number, reps: number | null) => void;
  onUpdateWeight: (exerciseId: string, setIndex: number, weight: string) => void;
  onUpdateAllWeights: (exerciseId: string, weight: string) => void;
  onUpdateNotes: (exerciseId: string, notes: string) => void;
  onSkip: (exerciseId: string, skip: boolean) => void;
}) {
  const [showWeightPopover, setShowWeightPopover] = useState(false);
  const [tempWeight, setTempWeight] = useState('');

  return (
    <tr className={`border-b ${entry.skipped ? 'opacity-50' : ''}`}>
      <td className="py-3 pr-4">
        <div>
          <p className="font-medium text-sm">{entry.exercise_name}</p>
          {entry.module_name && (
            <p className="text-xs text-gray-500">{entry.module_name}</p>
          )}
        </div>
      </td>
      <td className="text-center text-sm">
        <div>
          <p>{entry.prescribed_sets} × {entry.prescribed_reps}</p>
          {entry.prescribed_weight && (
            <p className="text-xs text-gray-500">@ {entry.prescribed_weight}</p>
          )}
        </div>
      </td>
      {Array.from({ length: entry.prescribed_sets }, (_, i) => (
        <td key={i} className="text-center px-2">
          <div className="space-y-1">
            <div className="flex items-center justify-center space-x-1">
              <input
                type="checkbox"
                checked={entry.completed_sets[i] || false}
                onChange={(e) => onUpdateSet(entry.exercise_id, i, e.target.checked)}
                disabled={entry.skipped}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <input
                type="number"
                value={entry.actual_reps[i] || ''}
                onChange={(e) => onUpdateReps(entry.exercise_id, i, parseInt(e.target.value) || null)}
                placeholder="Reps"
                disabled={entry.skipped}
                className="w-16 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="flex items-center justify-center">
              <input
                type="text"
                value={entry.actual_weight[i] || ''}
                onChange={(e) => onUpdateWeight(entry.exercise_id, i, e.target.value)}
                placeholder="Weight"
                disabled={entry.skipped}
                className="w-20 px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                onFocus={() => {
                  if (i === 0) {
                    setShowWeightPopover(true);
                    setTempWeight(entry.actual_weight[0] || '');
                  }
                }}
              />
            </div>
            {i === 0 && showWeightPopover && (
              <div className="absolute z-10 top-16 left-0 bg-white border rounded-lg shadow-lg p-2">
                <p className="text-xs text-gray-600 mb-1">Apply to all sets?</p>
                <div className="flex space-x-1">
                  <button
                    onClick={() => {
                      onUpdateAllWeights(entry.exercise_id, tempWeight);
                      setShowWeightPopover(false);
                    }}
                    className="px-2 py-1 text-xs bg-primary text-white rounded"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setShowWeightPopover(false)}
                    className="px-2 py-1 text-xs bg-gray-200 rounded"
                  >
                    No
                  </button>
                </div>
              </div>
            )}
          </div>
        </td>
      ))}
      {/* Add empty cells to pad to maxSets */}
      {Array.from({ length: (maxSets || entry.prescribed_sets) - entry.prescribed_sets }, (_, i) => (
        <td key={`empty-${i}`}></td>
      ))}
      <td className="text-center px-2">
        <input
          type="text"
          value={entry.notes || ''}
          onChange={(e) => onUpdateNotes(entry.exercise_id, e.target.value)}
          placeholder="Notes..."
          disabled={entry.skipped}
          className="w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </td>
      <td className="text-center">
        <input
          type="checkbox"
          checked={entry.skipped || false}
          onChange={(e) => onSkip(entry.exercise_id, e.target.checked)}
          className="rounded border-gray-300 text-red-500 focus:ring-red-500"
        />
      </td>
    </tr>
  );
}